import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { Fixed } from 'layouts';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, ArrowBack, Download, Refresh } from '@mui/icons-material';
import moment from 'moment';
import getSimulationById from 'redux/actions/Common/getSimulationById';
import getSimulationAgents from 'redux/actions/Common/getSimulationAgents';
import getSimulationSetup from 'redux/actions/Common/getSimulationSetup';
import pauseSimulation from 'redux/actions/Common/pauseSimulation';
import retriggerSimulation from 'redux/actions/Common/retriggerSimulation';
import terminateSimulation from 'redux/actions/Common/terminateSimulation';
import setSimulationAutoTickEnabled from 'redux/actions/Common/setSimulationAutoTickEnabled';
import getOrganizationById from 'redux/actions/Common/getOrganizationById';
import AgentTranscript, {
  downloadJson,
  fetchFullTranscript,
  safeFileName,
} from './components/AgentTranscript';
import SimulationSetup from './components/SimulationSetup';
import TickPromptEditor from './components/TickPromptEditor';
import AddBuyerDialog from './components/AddBuyerDialog';
import SimulationConversation from './components/SimulationConversation';
import {
  CopyableId,
  DetailRow,
  EnabledToolsChips,
  LifecycleButtons,
  isTerminalStatus,
  SIMULATIONS_PATH,
  StatusChips,
  formatSimDaySpeed,
  formatSimulatedElapsed,
  lifecycleAlert,
  llmProviderLabel,
} from './components/simulationUtils';

// Status is re-polled while the simulation can still change on its own
// (bootstrapping → running, or a running one going stale).
const AUTO_REFRESH_MS = 30000;
const LIVE_STATUSES = ['bootstrapping', 'running'];

const LIFECYCLE_ACTIONS = {
  pause: pauseSimulation,
  retrigger: retriggerSimulation,
  terminate: terminateSimulation,
};

const formatAbsolute = (epochMs) =>
  epochMs ? (
    <Typography variant={'body2'}>
      {moment(epochMs).format('DD MMM YYYY, hh:mm:ss A')}{' '}
      <Typography component="span" variant={'caption'} color={'text.secondary'}>
        ({moment(epochMs).fromNow()})
      </Typography>
    </Typography>
  ) : (
    <Typography variant={'body2'}>--</Typography>
  );

const SimulationDetail = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { simulationId } = useParams();

  const [simulation, setSimulation] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [togglingAutoTick, setTogglingAutoTick] = useState(false);
  const [orgName, setOrgName] = useState(null);
  const [alert, setAlert] = useState(null);
  const [currentTab, setCurrentTab] = useState('overview');
  // Kept mounted after the first visit so loaded pages survive tab switches.
  const [conversationVisited, setConversationVisited] = useState(false);

  const [agents, setAgents] = useState([]);
  const [fetchingAgents, setFetchingAgents] = useState(false);
  const [agentsError, setAgentsError] = useState(null);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const [setup, setSetup] = useState(null);
  const [fetchingSetup, setFetchingSetup] = useState(false);
  const [setupError, setSetupError] = useState(null);

  // Personas saved from this page this session, by agentConversationId — no
  // endpoint returns an agent's current persona, so this is the freshest copy.
  const [savedPersonas, setSavedPersonas] = useState({});
  const [showAddBuyer, setShowAddBuyer] = useState(false);

  const fetchSimulation = ({ silent = false } = {}) => {
    if (!silent) setFetching(true);
    dispatch(
      getSimulationById(
        simulationId,
        (data) => {
          setSimulation(data);
          setError(null);
          setFetching(false);
        },
        (message) => {
          setFetching(false);
          // A failed background poll shouldn't replace a page that loaded.
          if (silent) return;
          setError(message);
        },
      ),
    );
  };

  const fetchAgents = (selectAgentId) => {
    setFetchingAgents(true);
    setAgentsError(null);
    dispatch(
      getSimulationAgents(
        simulationId,
        (data) => {
          setAgents(data || []);
          setAgentsLoaded(true);
          setFetchingAgents(false);
          // Seller comes first — select it by default.
          setSelectedAgentId(
            (current) =>
              selectAgentId ||
              current ||
              data?.[0]?.agentConversationId ||
              null,
          );
        },
        (message) => {
          setAgentsError(message);
          setFetchingAgents(false);
        },
      ),
    );
  };

  const fetchSetup = () => {
    setFetchingSetup(true);
    setSetupError(null);
    dispatch(
      getSimulationSetup(
        simulationId,
        (data) => {
          setSetup(data || null);
          setFetchingSetup(false);
        },
        (message) => {
          setSetupError(message);
          setFetchingSetup(false);
        },
      ),
    );
  };

  const handleLifecycleAction = (action) => {
    if (updating) return;
    setUpdating(true);
    dispatch(
      LIFECYCLE_ACTIONS[action](
        simulationId,
        (data) => {
          setUpdating(false);
          setAlert(lifecycleAlert(action, data));
          // Backend is the source of truth for status/staleness.
          fetchSimulation({ silent: true });
        },
        (message) => {
          setUpdating(false);
          setAlert({ severity: 'error', message });
        },
      ),
    );
  };

  const handleToggleAutoTick = (event) => {
    if (togglingAutoTick) return;
    const enabled = event.target.checked;
    setTogglingAutoTick(true);
    dispatch(
      setSimulationAutoTickEnabled(
        simulationId,
        enabled,
        (data) => {
          setTogglingAutoTick(false);
          setSimulation((prev) =>
            prev ? { ...prev, autoTickEnabled: data?.autoTickEnabled } : prev,
          );
          setAlert({
            severity: 'info',
            message: data?.autoTickEnabled
              ? 'Auto-tick re-enabled — agents resume ticking on their normal schedule.'
              : 'Auto-tick disabled — agents only tick via Force tick now.',
          });
        },
        (message) => {
          setTogglingAutoTick(false);
          setAlert({ severity: 'error', message });
        },
      ),
    );
  };

  const handleSaveTickPrompts = (templates) => {
    setSimulation((prev) => (prev ? { ...prev, ...templates } : prev));
    setAlert({
      severity: 'info',
      message: 'Tick prompts saved — they apply from the next tick.',
    });
  };

  const handleTickPromptError = (message) =>
    setAlert({ severity: 'error', message });

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      const transcripts = [];
      for (const agent of agents) {
        const messages = await fetchFullTranscript(
          dispatch,
          simulationId,
          agent?.agentConversationId,
        );
        transcripts.push({
          ...agent,
          messageCount: (messages || []).length,
          messages: messages || [],
        });
      }
      downloadJson(
        `simulation-${safeFileName(simulationId)}-all-transcripts.json`,
        {
          simulation,
          exportedAt: new Date().toISOString(),
          agents: transcripts,
        },
      );
    } catch (e) {
      setAlert({
        severity: 'error',
        message: e?.message || 'Failed to download transcripts',
      });
    }
    setDownloadingAll(false);
  };

  useEffect(() => {
    setSimulation(null);
    setError(null);
    setAgents([]);
    setAgentsLoaded(false);
    setSelectedAgentId(null);
    setSetup(null);
    setSetupError(null);
    setConversationVisited(false);
    setSavedPersonas({});
    fetchSimulation();
  }, [simulationId]);

  useEffect(() => {
    if (!LIVE_STATUSES.includes(simulation?.status)) return undefined;
    const timer = setInterval(
      () => fetchSimulation({ silent: true }),
      AUTO_REFRESH_MS,
    );
    return () => clearInterval(timer);
  }, [simulation?.status, simulationId]);

  useEffect(() => {
    if (currentTab === 'conversation') setConversationVisited(true);
    // The conversation tab uses the roster to show names for sender emails.
    if (
      (currentTab === 'agents' || currentTab === 'conversation') &&
      !agentsLoaded &&
      !fetchingAgents
    ) {
      fetchAgents();
    }
    // The agents tab pre-fills persona editors from the creation setup.
    if (
      (currentTab === 'setup' || currentTab === 'agents') &&
      !setup &&
      !fetchingSetup &&
      !setupError
    ) {
      fetchSetup();
    }
  }, [currentTab, agentsLoaded]);

  useEffect(() => {
    const orgId = simulation?.organizationId;
    if (!orgId) return;
    dispatch(
      getOrganizationById(
        orgId,
        { fetchUsers: false, fetchTeams: false },
        (data) => setOrgName(data?.organizationName || null),
      ),
    );
  }, [simulation?.organizationId]);

  const personaFor = (agent) => {
    const saved = savedPersonas[agent?.agentConversationId];
    if (saved) return { ...saved, source: 'saved' };
    if (!setup) return null;
    if (agent?.role === 'seller') {
      return setup?.sellerPersonaOverride
        ? { text: setup.sellerPersonaOverride, source: 'creation' }
        : null;
    }
    // The roster's `name` is the buyer contact's name from creation.
    const contact = (setup?.buyerContacts || []).find(
      (buyer) =>
        buyer?.name?.trim()?.toLowerCase() ===
        agent?.name?.trim()?.toLowerCase(),
    );
    return contact?.persona
      ? { text: contact.persona, source: 'creation' }
      : null;
  };

  const handleBuyerAdded = (data) => {
    setShowAddBuyer(false);
    fetchAgents(data?.agentConversationId);
    // Only a populated aiServiceError is a real failure; a false join flag
    // alone just means the simulation wasn't running right now.
    setAlert(
      data?.aiServiceError
        ? {
            severity: 'warning',
            message: `${data?.name ||
              'Buyer'} was added, but joining the running simulation failed: ${
              data.aiServiceError
            }`,
          }
        : data?.aiServiceJoinTriggered
        ? {
            severity: 'success',
            message: `${data?.name || 'Buyer'} added and ticking`,
          }
        : {
            severity: 'info',
            message: `${data?.name ||
              'Buyer'} added — starts ticking when the simulation is next started or retriggered`,
          },
    );
  };

  const copied = () =>
    setAlert({ severity: 'success', message: 'Copied to clipboard' });

  const selectedAgent = agents.find(
    (agent) => agent?.agentConversationId === selectedAgentId,
  );

  const renderOverview = () => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <DetailRow label="Simulation ID">
        <CopyableId value={simulation?.simulationId} onCopied={copied} />
      </DetailRow>
      <DetailRow label="Seller Org">
        {orgName ? (
          <Typography variant={'body2'} fontWeight={'bold'}>
            {orgName}
          </Typography>
        ) : null}
        <CopyableId value={simulation?.organizationId} onCopied={copied} />
      </DetailRow>
      <DetailRow label="Deal ID">
        <CopyableId value={simulation?.dealId} onCopied={copied} />
      </DetailRow>
      <DetailRow label="Status">
        <StatusChips
          status={simulation?.status}
          stale={simulation?.stale}
          liveStatus={simulation?.liveStatus}
        />
      </DetailRow>
      <DetailRow label="Tick interval">
        <Typography variant={'body2'}>
          {simulation?.tickIntervalSeconds
            ? `${simulation.tickIntervalSeconds}s`
            : '--'}
        </Typography>
      </DetailRow>
      <DetailRow label="Simulated day">
        <Typography variant={'body2'}>
          {formatSimDaySpeed(simulation?.realMsPerSimDay)}
        </Typography>
        <Typography variant={'caption'} color={'text.secondary'}>
          Real time per simulated calendar day — fixed at creation.
        </Typography>
      </DetailRow>
      <DetailRow label="Simulated timeline">
        {(() => {
          const elapsed = formatSimulatedElapsed(
            simulation?.currentSimulatedTimeMs,
            simulation?.simulatedStartOn,
          );
          return elapsed ? (
            <>
              <Typography variant={'body2'}>
                {moment(simulation.currentSimulatedTimeMs).format(
                  'DD MMM YYYY',
                )}{' '}
                <Typography
                  component="span"
                  variant={'caption'}
                  color={'text.secondary'}
                >
                  ({elapsed.label} since the deal started{' '}
                  {moment(simulation.simulatedStartOn).format('DD MMM YYYY')})
                </Typography>
              </Typography>
              <Typography variant={'caption'} color={'text.secondary'}>
                The deal's fictional calendar date — not today's real date.
              </Typography>
            </>
          ) : (
            <Typography variant={'body2'}>--</Typography>
          );
        })()}
      </DetailRow>
      <DetailRow label="LLM">
        <Typography variant={'body2'}>
          {llmProviderLabel(simulation?.llmProvider)}{' '}
          {simulation?.llmModel ? (
            <Typography component="span" variant={'caption'}>
              ({simulation.llmModel})
            </Typography>
          ) : null}
        </Typography>
        <Typography variant={'caption'} color={'text.secondary'}>
          Fixed at creation — can’t be changed on an existing simulation.
        </Typography>
      </DetailRow>
      <DetailRow label="Created">
        {formatAbsolute(simulation?.createdOn)}
      </DetailRow>
      <DetailRow label="Last action">
        {formatAbsolute(simulation?.lastActionOn)}
      </DetailRow>
      <DetailRow label="Last heartbeat">
        {formatAbsolute(simulation?.lastHeartbeatOn)}
        {simulation?.lastHeartbeatInstanceId ? (
          <Typography
            variant={'caption'}
            color={'text.secondary'}
            display={'block'}
          >
            instance: {simulation.lastHeartbeatInstanceId}
          </Typography>
        ) : null}
      </DetailRow>
      <DetailRow label="Enabled tools">
        <EnabledToolsChips tools={simulation?.enabledTools} />
      </DetailRow>
    </Paper>
  );

  const renderTickPrompts = () => (
    <Box mt={2}>
      <TickPromptEditor
        simulationId={simulationId}
        sellerTemplate={simulation?.sellerTickPromptTemplate}
        buyerTemplate={simulation?.buyerTickPromptTemplate}
        disabled={isTerminalStatus(simulation?.status)}
        onSaved={handleSaveTickPrompts}
        onError={handleTickPromptError}
      />
    </Box>
  );

  const renderAgents = () =>
    fetchingAgents && !agentsLoaded ? (
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        gap={1}
        py={6}
      >
        <CircularProgress size={20} />
        <Typography variant={'body2'} color={'text.secondary'}>
          Loading agents...
        </Typography>
      </Box>
    ) : agentsError ? (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => fetchAgents()}>
            Retry
          </Button>
        }
      >
        {agentsError}
      </Alert>
    ) : agents.length === 0 ? (
      <Typography
        variant={'body2'}
        color={'text.secondary'}
        textAlign={'center'}
        py={6}
      >
        No agents in this simulation yet.
      </Typography>
    ) : (
      <Box
        display={'flex'}
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}
        alignItems={'flex-start'}
      >
        <Paper
          variant="outlined"
          sx={{
            width: { xs: '100%', md: 280 },
            flexShrink: 0,
            position: { md: 'sticky' },
            top: 0,
          }}
        >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
            px={2}
            py={1}
          >
            <Typography variant={'subtitle2'} fontWeight={'bold'}>
              Agents ({agents.length})
            </Typography>
            <Box display={'flex'} alignItems={'center'}>
              <Tooltip
                title={
                  isTerminalStatus(simulation?.status)
                    ? 'This simulation has finished'
                    : 'Add a buyer agent'
                }
              >
                <span>
                  <Button
                    size="small"
                    onClick={() => setShowAddBuyer(true)}
                    disabled={isTerminalStatus(simulation?.status)}
                    startIcon={<Add fontSize="small" />}
                  >
                    Buyer
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Download every agent's full transcript as one JSON file">
                <span>
                  <Button
                    size="small"
                    onClick={handleDownloadAll}
                    disabled={downloadingAll}
                    startIcon={
                      downloadingAll ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Download fontSize="small" />
                      )
                    }
                  >
                    All
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <List dense disablePadding>
            {agents.map((agent) => (
              <ListItemButton
                key={agent?.agentConversationId}
                selected={agent?.agentConversationId === selectedAgentId}
                onClick={() => setSelectedAgentId(agent?.agentConversationId)}
              >
                <ListItemText
                  primary={agent?.name || '--'}
                  secondary={agent?.email}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 'bold',
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: { wordBreak: 'break-all' },
                  }}
                />
                <Chip
                  size="small"
                  label={agent?.role || 'agent'}
                  color={agent?.role === 'seller' ? 'primary' : 'default'}
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
        <Paper variant="outlined" sx={{ flex: 1, p: 2, minWidth: 0 }}>
          {selectedAgent ? (
            <AgentTranscript
              simulationId={simulationId}
              agent={selectedAgent}
              simulationStatus={simulation?.status}
              persona={personaFor(selectedAgent)}
              onPersonaSaved={(agentId, personaPrompt) =>
                setSavedPersonas((existing) => ({
                  ...existing,
                  [agentId]: { text: personaPrompt, savedAt: Date.now() },
                }))
              }
              onNotify={setAlert}
            />
          ) : (
            <Typography variant={'body2'} color={'text.secondary'}>
              Select an agent to see their transcript.
            </Typography>
          )}
        </Paper>
      </Box>
    );

  return (
    <Fixed>
      <Box
        width={{ xs: '100vw', sm: '100vw', md: 'calc(100vw - 266px)' }}
        height={{ xs: '100vh', sm: '100vh', md: 'calc(100vh - 80px)' }}
        sx={{
          overflow: 'scroll',
          padding: '20px',
        }}
        display={'flex'}
        flexDirection={'column'}
        position={'relative'}
      >
        <Box
          display={'flex'}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={'space-between'}
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
        >
          <Box display={'flex'} alignItems={'center'} gap={1.5}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArrowBack fontSize="small" />}
              onClick={() => history.push(SIMULATIONS_PATH)}
            >
              Back
            </Button>
            <Box>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                <Typography variant={'h6'} fontWeight={'bold'}>
                  Simulation
                </Typography>
                {simulation ? (
                  <StatusChips
                    status={simulation?.status}
                    stale={simulation?.stale}
                    liveStatus={simulation?.liveStatus}
                  />
                ) : null}
              </Box>
              <CopyableId value={simulationId} onCopied={copied} />
            </Box>
          </Box>
          {simulation ? (
            <Box display={'flex'} alignItems={'center'} gap={1}>
              <LifecycleButtons
                status={simulation?.status}
                updating={updating}
                onAction={handleLifecycleAction}
              />
              <Tooltip
                title={
                  simulation?.autoTickEnabled === false
                    ? 'Agents only tick via Force tick. Re-enable to resume their normal schedule.'
                    : 'Agents tick automatically on their normal schedule. Disable to only allow manual Force tick.'
                }
              >
                <FormControlLabel
                  sx={{ ml: 1 }}
                  control={
                    <Switch
                      size="small"
                      checked={simulation?.autoTickEnabled !== false}
                      onChange={handleToggleAutoTick}
                      disabled={togglingAutoTick || isTerminalStatus(simulation?.status)}
                    />
                  }
                  label={
                    <Typography variant={'body2'}>
                      {togglingAutoTick ? (
                        <CircularProgress size={12} sx={{ mr: 0.5 }} />
                      ) : null}
                      Auto-tick
                    </Typography>
                  }
                />
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                onClick={() => fetchSimulation({ silent: true })}
                startIcon={<Refresh fontSize="small" />}
              >
                Refresh
              </Button>
            </Box>
          ) : null}
        </Box>

        {fetching && !simulation ? (
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={1}
            py={6}
          >
            <CircularProgress size={20} />
            <Typography variant={'body2'} color={'text.secondary'}>
              Loading simulation...
            </Typography>
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => fetchSimulation()}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : simulation ? (
          <>
            <Tabs
              value={currentTab}
              onChange={(e, value) => setCurrentTab(value)}
              sx={{ mt: 2, mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="overview" label="Overview" />
              <Tab value="agents" label="Agents & Transcript" />
              <Tab value="conversation" label="Conversation" />
              <Tab value="setup" label="Setup" />
            </Tabs>
            {currentTab === 'overview' ? (
              <>
                {renderOverview()}
                {renderTickPrompts()}
              </>
            ) : currentTab === 'agents' ? (
              renderAgents()
            ) : currentTab === 'conversation' ? null : (
              <SimulationSetup
                setup={setup}
                fetching={fetchingSetup}
                error={setupError}
                onRefresh={fetchSetup}
                onCopied={copied}
              />
            )}
            {conversationVisited ? (
              <Box display={currentTab === 'conversation' ? 'block' : 'none'}>
                <SimulationConversation
                  simulationId={simulationId}
                  agents={agents}
                />
              </Box>
            ) : null}
          </>
        ) : null}

        <AddBuyerDialog
          open={showAddBuyer}
          simulationId={simulationId}
          simulationStatus={simulation?.status}
          onClose={() => setShowAddBuyer(false)}
          onAdded={handleBuyerAdded}
        />

        <Snackbar
          open={Boolean(alert)}
          autoHideDuration={alert?.severity === 'warning' ? 10000 : 4000}
          onClose={() => setAlert(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setAlert(null)}
            severity={alert?.severity || 'info'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {alert?.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fixed>
  );
};

export default SimulationDetail;
